/**
 * عوض کردنِ رمز از داخلِ پروفایل (WHOSUP-367) — مشترکِ اپ و وب.
 *
 * فرقش با «رمزم را فراموش کردم»: آن‌جا کسی رمزش را نمی‌داند و ایمیل
 * ثابت می‌کند صاحبِ حساب است. این‌جا کاربر وارد است، پس رمزِ **فعلی** را
 * می‌پرسیم. بدونِ آن، هر کسی که یک لحظه گوشیِ بازِ کسی دستش بیفتد می‌تواند
 * رمز را عوض کند و صاحبِ حساب را بیرون بگذارد.
 *
 * سنجیدنِ رمزِ فعلی با `signInWithPassword` است — Supabase راهِ دیگری برای
 * «این رمز درست است؟» ندارد، و چون همان کاربر است، نتیجه‌اش فقط یک نشستِ
 * تازه برای همان آدم است. `secure_password_change` در `supabase/config.toml`
 * خاموش است، پس `updateUser` خودش رمزِ فعلی را نمی‌خواهد؛ این سنجش کارِ ماست.
 *
 * فقط حسابی که با ایمیل و رمز ساخته شده این گزینه را می‌بیند
 * (`hasPasswordLogin`). حسابِ گوگل/اپل رمزی ندارد که «عوض» شود، و گذاشتنِ رمز
 * روی آن (مثلاً روی ایمیلِ پنهانِ اپل) کارِ دیگری است با تصمیم‌های دیگر.
 */
import type { User } from '@supabase/supabase-js';
import { passwordProblem } from './password.ts';

/** آیا این حساب با ایمیل و رمز وارد می‌شود. `identities` اولویت دارد؛ `app_metadata` پشتیبان. */
export function hasPasswordLogin(user: Pick<User, 'identities' | 'app_metadata' | 'email'> | null | undefined): boolean {
  if (!user?.email) return false;
  if (user.identities?.some((i) => i.provider === 'email')) return true;
  const meta = user.app_metadata ?? {};
  if (meta.provider === 'email') return true;
  return Array.isArray(meta.providers) && meta.providers.includes('email');
}

export type ChangePasswordProblem =
  /** رمزِ فعلی خالی است. */
  | 'noCurrent'
  /** همان دو حالتِ `passwordProblem`. */
  | 'short'
  | 'simple'
  /** دو بارِ رمزِ تازه یکی نیست. */
  | 'mismatch'
  /** رمزِ تازه همان رمزِ فعلی است. */
  | 'same';

/** مشکلِ فرم، پیش از هر درخواستی به سرور؛ `null` یعنی برو. */
export function changePasswordProblem(current: string, next: string, again: string): ChangePasswordProblem | null {
  if (!current) return 'noCurrent';
  const pw = passwordProblem(next);
  if (pw) return pw;
  if (next !== again) return 'mismatch';
  if (next === current) return 'same';
  return null;
}

/** کلیدِ ترجمه‌ی هر مشکل — دو فرمِ اپ و وب از همین می‌خوانند. */
export const CHANGE_PASSWORD_MESSAGE: Record<ChangePasswordProblem | 'wrongCurrent', string> = {
  noCurrent: 'changePasswordNeedCurrent',
  short: 'authPasswordShort',
  simple: 'authPasswordSimple',
  mismatch: 'authPasswordMismatch',
  same: 'changePasswordSame',
  wrongCurrent: 'changePasswordWrongCurrent',
};

/** همان تکه‌ای از کلاینتِ Supabase که این‌جا لازم است — تا تست بی‌شبکه بماند. */
export interface PasswordAuth {
  signInWithPassword(c: { email: string; password: string }): Promise<{ error: { message: string; code?: string; status?: number } | null }>;
  updateUser(a: { password: string }): Promise<{ error: { message: string } | null }>;
}

/** خطای «ایمیل یا رمز غلط» از Supabase — کد در نسخه‌های تازه، متن در قدیمی‌ها. */
export function isWrongPasswordError(e: { message: string; code?: string } | null | undefined): boolean {
  if (!e) return false;
  return e.code === 'invalid_credentials' || /invalid login credentials/i.test(e.message);
}

/**
 * رمزِ فعلی را می‌سنجد و رمزِ تازه را می‌گذارد. فرم باید پیش‌تر از
 * `changePasswordProblem` رد شده باشد. رمزِ فعلیِ غلط خطا نیست، جواب است
 * (`'wrongCurrent'`)؛ هر چیزِ دیگری (شبکه، سرور) پرتاب می‌شود.
 */
export async function changeMyPassword(
  auth: PasswordAuth, email: string, current: string, next: string,
): Promise<'ok' | 'wrongCurrent'> {
  const check = await auth.signInWithPassword({ email, password: current });
  if (check.error) {
    if (isWrongPasswordError(check.error)) return 'wrongCurrent';
    throw new Error(check.error.message);
  }
  const { error } = await auth.updateUser({ password: next });
  if (error) throw new Error(error.message);
  return 'ok';
}
